/**
 * Self-service branch admin card on the broker portal. Lights up when the
 * authed referrer has ≥1 branch in `branch_admin_of`. Lets them:
 *   - See who's in each branch they admin (name + login pill + admin badge).
 *   - Edit each branch's address + payment/branding overrides via the
 *     existing `PUT /branches/{id}` endpoint (which now accepts an
 *     `x-referrer-token` when that referrer is admin of the branch).
 *
 * Phase 2 keeps this read + light-edit only — the solicitor admin still
 * owns CRUD (create/delete + ACL assignment).
 */
import { useEffect, useState } from "react";
import { Buildings, Check, PencilSimple, X, User, Plus, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import API from "../../lib/api";

export default function BranchAdminCard({ token }) {
  const headers = { "x-referrer-token": token };
  const [data, setData] = useState({ is_admin: false, branches: [] });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null); // branch row expanded for edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Per-branch "Add member" dropdown picks — keyed by branch id so multiple
  // branches can have independent selections without state collisions.
  const [addPick, setAddPick] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: d } = await API.get("/branch-admin/me", { headers });
      setData(d || {});
    } catch (err) {
      // 401 just means no token — render nothing rather than spam toasts.
      if (err.response?.status !== 401) toast.error(err.response?.data?.detail || "Failed to load branch admin data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  if (loading || !data.is_admin || !data.branches?.length) return null;
  const startEdit = (b) => {
    setOpenId(b.id);
    setForm({
      address: b.address || "",
      bank_name: b.bank_name || "",
      account_name: b.account_name || "",
      sort_code: b.sort_code || "",
      account_number: b.account_number || "",
    });
  };
  const cancel = () => { setOpenId(null); setForm({}); };
  const saveBranch = async (bid) => {
    setSaving(true);
    try {
      await API.put(`/branches/${bid}`, form, { headers });
      toast.success("Branch saved");
      cancel();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const assignMember = async (memberId, branchId, friendlyDest) => {
    try {
      await API.post("/branch-admin/assign-member", { member_id: memberId, branch_id: branchId || null }, { headers });
      toast.success(branchId ? `Moved to ${friendlyDest}` : `Removed from branch`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update member");
    }
  };

  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="branch-admin-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Buildings weight="bold" className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Branch admin <span className="text-slate-400 font-normal">· {data.branches.length} branch{data.branches.length === 1 ? "" : "es"}</span>
          </h3>
        </div>
        <span className="text-[11px] text-slate-400">{data.company}</span>
      </div>
      <ul className="space-y-3">
        {data.branches.map((b) => (
          <li key={b.id} className="border border-slate-100 rounded-md p-3" data-testid={`admin-branch-${b.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div>
                <p className="text-sm font-semibold text-slate-900">{b.name}</p>
                <p className="text-[11px] text-slate-500">{b.member_count} member{b.member_count === 1 ? "" : "s"}{b.address ? ` · ${b.address}` : ""}</p>
              </div>
              {openId === b.id ? (
                <div className="flex items-center gap-1">
                  <button onClick={() => saveBranch(b.id)} disabled={saving} className="w-7 h-7 flex items-center justify-center rounded hover:bg-emerald-100 text-emerald-600 disabled:opacity-40" data-testid={`admin-branch-save-${b.id}`}><Check weight="bold" className="w-3.5 h-3.5" /></button>
                  <button onClick={cancel} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500" data-testid={`admin-branch-cancel-${b.id}`}><X weight="bold" className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button onClick={() => startEdit(b)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500" title="Edit branch" data-testid={`admin-branch-edit-${b.id}`}><PencilSimple weight="bold" className="w-3.5 h-3.5" /></button>
              )}
            </div>
            {openId === b.id && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid={`admin-branch-form-${b.id}`}>
                <div className="col-span-2">
                  <Label className="text-[11px] text-slate-500">Address</Label>
                  <Input className="h-8 text-xs" value={form.address || ""} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Address (optional)" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Bank name</Label>
                  <Input className="h-8 text-xs" value={form.bank_name || ""} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} placeholder="Inherits from company default" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Account name</Label>
                  <Input className="h-8 text-xs" value={form.account_name || ""} onChange={(e) => setForm((f) => ({ ...f, account_name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Sort code</Label>
                  <Input className="h-8 text-xs" value={form.sort_code || ""} onChange={(e) => setForm((f) => ({ ...f, sort_code: e.target.value }))} placeholder="00-00-00" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Account number</Label>
                  <Input className="h-8 text-xs" value={form.account_number || ""} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} placeholder="12345678" />
                </div>
                <p className="col-span-2 text-[10px] text-slate-400 italic mt-1">Blank fields fall back to the company-wide default.</p>
              </div>
            )}
            {b.members?.length > 0 && (
              <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                {b.members.map((m) => (
                  <li key={m.id} className="flex items-center justify-between text-xs group" data-testid={`admin-member-${m.id}`}>
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <User weight="bold" className="w-3 h-3 text-slate-400" />
                      {m.name}
                      {m.is_admin_here && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">Admin</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{m.email || ""}</span>
                      {/* Remove only — admins of THIS branch can pull anyone
                          off it (including themselves). Self-removal is
                          intentionally allowed; the solicitor can re-grant
                          admin if needed. */}
                      {m.id !== data.referrer_id && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove ${m.name} from ${b.name}?`)) assignMember(m.id, null);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:bg-rose-100 hover:text-rose-600"
                          title={`Remove ${m.name} from ${b.name}`}
                          data-testid={`admin-member-remove-${m.id}`}
                        ><X weight="bold" className="w-3 h-3" /></button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {/* Add-member picker — lists every colleague NOT currently in
                this branch (other branches + un-branched). The admin picks
                someone + clicks the green arrow to assign. */}
            {(() => {
              const eligible = (data.company_members || []).filter(
                (cm) => cm.branch_id !== b.id && cm.id !== data.referrer_id
              );
              if (eligible.length === 0) return null;
              const picked = addPick[b.id] || "";
              return (
                <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2" data-testid={`admin-add-member-${b.id}`}>
                  <Plus weight="bold" className="w-3 h-3 text-slate-400 shrink-0" />
                  <select
                    value={picked}
                    onChange={(e) => setAddPick((p) => ({ ...p, [b.id]: e.target.value }))}
                    className="flex-1 h-7 px-2 rounded border border-slate-200 text-xs bg-white"
                    data-testid={`admin-add-select-${b.id}`}
                  >
                    <option value="">— Add a colleague to {b.name} —</option>
                    {eligible.map((cm) => {
                      const fromBranch = (data.branches.find((bb) => bb.id === cm.branch_id)?.name) || "no branch";
                      return (
                        <option key={cm.id} value={cm.id}>{cm.name} (from {fromBranch})</option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => {
                      if (!picked) return;
                      assignMember(picked, b.id, b.name);
                      setAddPick((p) => ({ ...p, [b.id]: "" }));
                    }}
                    disabled={!picked}
                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-emerald-100 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Add to branch"
                    data-testid={`admin-add-commit-${b.id}`}
                  ><ArrowsLeftRight weight="bold" className="w-3.5 h-3.5" /></button>
                </div>
              );
            })()}
          </li>
        ))}
      </ul>
    </div>
  );
}
